"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import type { UrgencyLevel } from "@/lib/problems-data";

interface ProblemSummary {
  slug: string;
  title: string;
  description: string;
  urgency_level: UrgencyLevel;
  recommended_trade_category: string;
}

const urgencyDot = {
  cosmetic: "bg-green-500",
  fix_soon: "bg-amber-500",
  urgent: "bg-red-500",
};

export function ProblemsSearch({ problems }: { problems: ProblemSummary[] }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    return problems.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [query, problems]);

  return (
    <div className="mb-10">
      <div className="max-w-xl mx-auto">
        <div className="relative">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Describe your problem, e.g. 'damp patch on ceiling' or 'boiler not working'"
            className="w-full pl-10 pr-4 py-3 text-sm rounded-md border border-stone bg-white focus:outline-none focus:border-forest focus:ring-1 focus:ring-forest placeholder:text-text-muted"
          />
        </div>
      </div>

      {query.trim().length > 0 && (
        <div className="max-w-xl mx-auto mt-3">
          {filtered.length > 0 ? (
            <div className="bg-white rounded-md border border-stone divide-y divide-stone overflow-hidden">
              {filtered.map((p) => (
                <Link
                  key={p.slug}
                  href={`/problems/${p.slug}`}
                  className="flex items-start gap-3 px-4 py-3 no-underline hover:bg-warm-bg transition-colors"
                >
                  <span className={`shrink-0 mt-1.5 w-2 h-2 rounded-full ${urgencyDot[p.urgency_level]}`} />
                  <div>
                    <span className="text-sm font-semibold text-charcoal">{p.title}</span>
                    <p className="text-xs text-text-muted line-clamp-1">{p.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-text-muted text-center py-4">
              No matching problems found. Try{" "}
              <Link href="/snap" className="text-copper no-underline hover:underline">
                Snap and Fix
              </Link>{" "}
              for an AI-powered diagnosis.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
